import "./Equipo.css";
import { AiOutlineTeam } from "react-icons/ai";
import { FaBriefcase, FaBuilding, FaClock, FaEnvelope, FaIdBadge, FaPhoneAlt, FaUser, FaWhatsapp } from "react-icons/fa";
import React, { useContext, useRef, useState, useEffect } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";

import { getApiUrl } from "../../../utils/apiUrl";
import { getCsrfToken } from "../../../utils/getCsrfToken";
import PersonAvatar from "../../0. Utilidades/PersonAvatar";
import { UserContext } from "../../0. Header/UserContext";

const EMPTY_FORM = {
  nombre: "", rut: "", correo: "", correo_laboral: "", celular: "", profesion: "", cargo: "",
  notas: "", organizacion_id: "", grupo_whatsapp_ids: [],
};

const LOGIN_DATE_FORMATTER = new Intl.DateTimeFormat("es-CL", {
  dateStyle: "medium",
  timeStyle: "short",
});

const formatLastLogin = (value) => {
  if (!value) return "Nunca ha ingresado";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Ingreso sin fecha disponible" : LOGIN_DATE_FORMATTER.format(date);
};

const EquipoModal = () => {
  const currentUser = useContext(UserContext);
  const modalContainer = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [profesionales, setProfesionales] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [organizations, setOrganizations] = useState([]);
  const [availableWhatsappGroups, setAvailableWhatsappGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const [togglingProfessionalId, setTogglingProfessionalId] = useState(null);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [whatsappExpanded, setWhatsappExpanded] = useState(false);
  const notesRef = useRef(null);
  const [notesCanExpand, setNotesCanExpand] = useState(false);
  const canManage = (currentUser.roles || []).some((role) => ["Soporte", "Fundadora"].includes(role));
  const ownsSelectedProfessional = Boolean(
    selectedProfessional?.usuario?.id
    && String(selectedProfessional.usuario.id) === String(currentUser.id)
  );
  const canEditSelectedProfessional = canManage || ownsSelectedProfessional;
  const actionPending = saving || togglingProfessionalId !== null;

  useEffect(() => {
    setNotesExpanded(false);
    setWhatsappExpanded(false);
    setNotesCanExpand(false);
    const frame = window.requestAnimationFrame(() => {
      const notes = notesRef.current;
      if (notes) setNotesCanExpand(notes.scrollHeight > notes.clientHeight + 1);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [selectedProfessional?.id, selectedProfessional?.notas]);

  const loadProfessionals = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await fetch(getApiUrl(`api/profesionales/?activo=${showInactive ? "false" : "true"}`), { credentials: "include" });
      if (!response.ok) throw new Error();
      const data = await response.json();
      setProfesionales(Array.isArray(data) ? data : data.results || []);
    } catch (_error) {
      setError("No se pudo cargar el equipo.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) loadProfessionals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, showInactive]);

  const equipoKpis = [
    { title: showInactive ? "Inactivos" : "Equipo Actual", value: profesionales.length },
  ];

  const openEditor = async (professional = null) => {
    if (actionPending) return;
    setEditorOpen(true);
    setEditing(professional);
    setForm(professional ? {
      nombre: professional.nombre || "",
      rut: professional.rut || "",
      correo: professional.correo || "",
      correo_laboral: professional.correo_laboral || "",
      celular: professional.celular || "",
      profesion: professional.profesion || "",
      cargo: professional.cargo || "",
      notas: professional.notas || "",
      organizacion_id: professional.organizacion ? String(professional.organizacion) : "",
      grupo_whatsapp_ids: (professional.grupos_whatsapp || []).map((group) => group.id),
    } : EMPTY_FORM);
    setError("");
    try {
      const [organizationsResponse, whatsappGroupsResponse] = await Promise.all([
        fetch(getApiUrl("api/profesionales/organizaciones/"), { credentials: "include" }),
        fetch(getApiUrl("api/whatsapp/groups/"), { credentials: "include" }),
      ]);
      if (organizationsResponse.ok) setOrganizations(await organizationsResponse.json());
      if (whatsappGroupsResponse.ok) setAvailableWhatsappGroups(await whatsappGroupsResponse.json());
    } catch (_error) {
      setAvailableWhatsappGroups([]);
    }
  };

  const saveProfessional = async () => {
    if (actionPending || !form.nombre.trim()) return;
    setSaving(true);
    setError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/profesionales/${editing ? `${editing.id}/` : ""}`), {
        method: editing ? "PATCH" : "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({
          ...form,
          nombre: form.nombre.trim(),
          rut: form.rut.trim() || null,
          correo: form.correo.trim() || null,
          correo_laboral: form.correo_laboral.trim() || null,
          celular: form.celular.trim() || null,
          profesion: form.profesion.trim() || null,
          cargo: form.cargo.trim() || null,
          notas: form.notas.trim() || null,
          organizacion_id: form.organizacion_id || null,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = Object.values(data).flat().find((value) => typeof value === "string");
        throw new Error(message || "No se pudo guardar el profesional.");
      }
      await loadProfessionals();
      setEditing(null);
      setEditorOpen(false);
      setSelectedProfessional(null);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const deactivateProfessional = async (professional) => {
    if (actionPending) return;
    setTogglingProfessionalId(professional.id);
    setError("");
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(getApiUrl(`api/profesionales/${professional.id}/`), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ user_id: null }),
      });
      if (!response.ok) throw new Error();
      await loadProfessionals();
      setSelectedProfessional(null);
    } catch (_error) {
      setError("No se pudo desactivar a la profesional.");
    } finally {
      setTogglingProfessionalId(null);
    }
  };

  return (
    <div id="equipo" ref={modalContainer}>
      <button className="equipo-open-button" onClick={() => setIsOpen(true)}>
        <AiOutlineTeam /> Revisar Equipo
      </button>

      <Modal container={modalContainer.current} show={isOpen} onHide={() => { if (!actionPending) setIsOpen(false); }} size="xl" centered dialogClassName="equipo-management-modal">
        <Modal.Header closeButton={!actionPending}><Modal.Title>Equipo RedMaestra</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="equipo-toolbar">
            <div className="equipo-kpi-grid">{equipoKpis.map((kpi) => <div key={kpi.title} className="equipo-kpi-card"><span>{kpi.title}</span><strong>{kpi.value}</strong></div>)}</div>
            {canManage && <div className="equipo-actions">
              <button type="button" disabled={actionPending} onClick={() => setShowInactive((value) => !value)}>{showInactive ? "Ver activos" : "Ver inactivos"}</button>
              <button type="button" disabled={actionPending} onClick={() => openEditor()}>+ Nuevo profesional</button>
            </div>}
          </div>
          {error && <Alert variant="danger">{error}</Alert>}
          {loading ? <div className="equipo-loading"><Spinner animation="border" /></div> : (
            <div className="profesional-list">
              {profesionales.map((profesional) => (
                <article
                  key={profesional.id}
                  className="profesional-card"
                  role="button"
                  tabIndex={actionPending ? -1 : 0}
                  aria-disabled={actionPending}
                  onClick={() => { if (!actionPending) setSelectedProfessional(profesional); }}
                  onKeyDown={(event) => { if (!actionPending && event.key === "Enter") setSelectedProfessional(profesional); }}
                >
                  <PersonAvatar src={profesional.imagen} name={profesional.nombre} alt={`Fotografía de ${profesional.nombre}`} className="profesional-image" tone="profesional" />
                  <div className="profesional-info">
                    <h3>{profesional.nombre}</h3>
                    <span className="profesional-user"><FaUser /> {profesional.usuario?.username || "Sin cuenta asignada"}</span>
                    <span className="profesional-last-login"><FaClock /> Último ingreso: {formatLastLogin(profesional.usuario?.last_login)}</span>
                    <div className="group-badges">
                      {(profesional.groups || []).length ? profesional.groups.map((group) => (
                        <span key={group.name} className="group-badge" style={{ "--group-color": group.color || "#6e6e6e" }}>{group.name}</span>
                      )) : <span className="group-badge no-group">Sin permisos asignados</span>}
                    </div>
                  </div>
                  <div className="profesional-contact">
                    <strong><FaBriefcase /> {profesional.cargo || "Cargo no informado"}</strong>
                    <span><FaEnvelope /> {profesional.correo_laboral || "Correo laboral no informado"}</span>
                    <span><FaPhoneAlt /> {profesional.celular || "Teléfono no disponible"}</span>
                  </div>
                </article>
              ))}
              {!profesionales.length && <p>No hay profesionales en esta lista.</p>}
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal container={modalContainer.current} show={Boolean(selectedProfessional)} onHide={() => { if (!actionPending) setSelectedProfessional(null); }} centered dialogClassName="professional-detail-modal">
        <Modal.Header closeButton={!actionPending}><Modal.Title>Detalle profesional</Modal.Title></Modal.Header>
        <Modal.Body>
          {selectedProfessional && <>
            <div className="professional-detail-hero">
              <PersonAvatar src={selectedProfessional.imagen} name={selectedProfessional.nombre} alt={`Fotografía de ${selectedProfessional.nombre}`} className="professional-detail-avatar" tone="profesional" />
              <div>
                <h3>{selectedProfessional.nombre}</h3>
                <div className="group-badges">
                  {(selectedProfessional.groups || []).length ? selectedProfessional.groups.map((group) => (
                    <span key={group.name} className="group-badge" style={{ "--group-color": group.color || "#6e6e6e" }}>{group.name}</span>
                  )) : <span className="group-badge no-group">Sin permisos asignados</span>}
                </div>
              </div>
            </div>
            <div className="professional-detail-grid">
              <div><FaIdBadge /><span>Cargo</span><strong>{selectedProfessional.cargo || "No informado"}</strong></div>
              <div><FaEnvelope /><span>Correo laboral</span><strong>{selectedProfessional.correo_laboral || "No informado"}</strong></div>
              <div><FaPhoneAlt /><span>Celular</span><strong>{selectedProfessional.celular || "No informado"}</strong></div>
              <div><FaBuilding /><span>Organización</span><strong>{selectedProfessional.organizacion_nombre || "No informada"}</strong></div>
              <div><FaBriefcase /><span>Profesión</span><strong>{selectedProfessional.profesion || "No informada"}</strong></div>
              <div><FaEnvelope /><span>Correo personal</span><strong>{selectedProfessional.correo || "No informado"}</strong></div>
            </div>
            <div className="professional-whatsapp-block">
              <div className="professional-whatsapp-heading">
                <span>WhatsApp</span>
                <strong><FaWhatsapp /> Grupos de WhatsApp</strong>
              </div>
              {(selectedProfessional.grupos_whatsapp || []).length ? (
                <>
                  <ul>{selectedProfessional.grupos_whatsapp.slice(0, whatsappExpanded ? undefined : 3).map((group) => <li key={group.id}>{group.nombre}</li>)}</ul>
                  {selectedProfessional.grupos_whatsapp.length > 3 && (
                    <button type="button" className="professional-whatsapp-toggle" onClick={() => setWhatsappExpanded((value) => !value)} aria-expanded={whatsappExpanded}>
                      {whatsappExpanded ? "Ver menos" : `Ver más (${selectedProfessional.grupos_whatsapp.length - 3})`}
                    </button>
                  )}
                </>
              ) : <p>Sin grupos asignados.</p>}
            </div>
            {selectedProfessional.notas && (
              <div className={`professional-detail-notes${notesExpanded ? " expanded" : ""}`}>
                <span>Notas internas</span>
                <div className={`professional-notes-copy${notesCanExpand && !notesExpanded ? " has-overflow" : ""}`}>
                  <p ref={notesRef} className={notesExpanded ? "" : "is-collapsed"}>{selectedProfessional.notas}</p>
                </div>
                {notesCanExpand && (
                  <button type="button" className="professional-notes-toggle" onClick={() => setNotesExpanded((value) => !value)}>
                    {notesExpanded ? "Ver menos" : "Ver más"}
                  </button>
                )}
              </div>
            )}
          </>}
        </Modal.Body>
        {canEditSelectedProfessional && selectedProfessional && (
          <Modal.Footer>
            {canEditSelectedProfessional && <button type="button" disabled={actionPending} onClick={() => { const professional = selectedProfessional; setSelectedProfessional(null); openEditor(professional); }}>Editar</button>}
            {canManage && selectedProfessional.activo ? (
              <button
                type="button"
                className="danger"
                disabled={actionPending}
                aria-busy={togglingProfessionalId === selectedProfessional.id}
                onClick={() => deactivateProfessional(selectedProfessional)}
              >
                {togglingProfessionalId === selectedProfessional.id && <Spinner as="span" animation="border" size="sm" aria-hidden="true" />}
                {togglingProfessionalId === selectedProfessional.id ? "Desactivando..." : "Desactivar"}
              </button>
            ) : canManage ? (
              <span className="professional-reactivation-note">Asigna una cuenta al editar para reactivar.</span>
            ) : null}
          </Modal.Footer>
        )}
      </Modal>

      <Modal container={modalContainer.current} show={editorOpen} onHide={() => { if (!saving) { setEditorOpen(false); setEditing(null); setForm(EMPTY_FORM); } }} centered dialogClassName="professional-editor-modal">
        <Modal.Header closeButton={!saving}><Modal.Title>{editing ? "Editar profesional" : "Nuevo profesional"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="professional-editor-intro">
            <span><FaUser /></span>
            <div>
              <strong>{editing ? "Actualizar información profesional" : "Registrar una nueva profesional"}</strong>
              <p>{canManage ? "Completa los antecedentes de la ficha profesional." : "Actualiza los datos de tu propia ficha profesional."}</p>
            </div>
          </div>
          <fieldset className="equipo-edit-form" disabled={saving} aria-busy={saving}>
            <section className="equipo-form-section">
              <h4><FaIdBadge /> Identificación</h4>
              <div className="equipo-form-grid">
                <label><span>Nombre *</span><input value={form.nombre} onChange={(event) => setForm({ ...form, nombre: event.target.value })} /></label>
                <label><span>RUT</span><input value={form.rut} onChange={(event) => setForm({ ...form, rut: event.target.value })} /></label>
                <label><span>Correo laboral</span><input type="email" value={form.correo_laboral} onChange={(event) => setForm({ ...form, correo_laboral: event.target.value })} /></label>
                <label><span>Celular</span><input value={form.celular} onChange={(event) => setForm({ ...form, celular: event.target.value })} /></label>
                <label><span>Cargo</span><input value={form.cargo} onChange={(event) => setForm({ ...form, cargo: event.target.value })} /></label>
                <label><span>Organización</span><select value={form.organizacion_id} onChange={(event) => setForm({ ...form, organizacion_id: event.target.value })}><option value="">Sin organización</option>{organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.nombre}</option>)}</select></label>
              </div>
            </section>
            <section className="equipo-form-section">
              <h4><FaBriefcase /> Datos personales</h4>
              <div className="equipo-form-grid">
                <label><span>Profesión</span><input value={form.profesion} onChange={(event) => setForm({ ...form, profesion: event.target.value })} /></label>
                <label><span>Correo personal</span><input type="email" value={form.correo} onChange={(event) => setForm({ ...form, correo: event.target.value })} /></label>
              </div>
            </section>
            <section className="equipo-form-section equipo-whatsapp-section">
              <h4><FaWhatsapp /> Grupos de WhatsApp</h4>
              <div className="equipo-whatsapp-options">
                {availableWhatsappGroups.map((group) => (
                  <label key={group.id}>
                    <input
                      type="checkbox"
                      checked={form.grupo_whatsapp_ids.includes(group.id)}
                      onChange={() => setForm((current) => ({
                        ...current,
                        grupo_whatsapp_ids: current.grupo_whatsapp_ids.includes(group.id)
                          ? current.grupo_whatsapp_ids.filter((id) => id !== group.id)
                          : [...current.grupo_whatsapp_ids, group.id],
                      }))}
                    />
                    <span>{group.nombre}</span>
                  </label>
                ))}
                {!availableWhatsappGroups.length && <p>No hay grupos disponibles.</p>}
              </div>
            </section>
            <section className="equipo-form-section">
              <h4><FaIdBadge /> Notas internas</h4>
              <label className="equipo-notes"><span>Información para el equipo</span><textarea rows="3" value={form.notas} onChange={(event) => setForm({ ...form, notas: event.target.value })} /></label>
            </section>
          </fieldset>
          {error && <Alert variant="danger">{error}</Alert>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" disabled={saving} onClick={() => { setEditorOpen(false); setEditing(null); setForm(EMPTY_FORM); }}>Cancelar</button>
          <button type="button" disabled={saving || !form.nombre.trim()} aria-busy={saving} onClick={saveProfessional}>
            {saving && <Spinner as="span" animation="border" size="sm" aria-hidden="true" />}
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EquipoModal;
